/* ==========================================================================
   Aurmina - Well overlay
   REVISION c
   Revision:   c - 30 Aug 2026
   Goes in:    assets/aurmina-well-overlay.js
   Used by:    blocks/aurmina-well-overlay.liquid rev b

   WHAT IT DOES, IN ONE SENTENCE

   Moves the badge and size-label element out of the product details column
   and into .product-information__media, so it sits over the product photo
   the way the mockup draws it.

   WHY A SCRIPT RATHER THAN CSS POSITIONING

   Atelier's product page is one CSS subgrid: .product-information__grid
   holds .product-information__media and .product-details as siblings. A
   block can only be placed inside .product-details, and nothing placed
   there can be positioned into the media column reliably:

     - absolute positioning anchors to the nearest POSITIONED ancestor, and
       _product-details renders through Atelier's `group` snippet. If any
       wrapper in that chain carries position: relative, the badge lands in
       the top-left of the TEXT column instead of the photograph, with no
       error and no obvious cause.
     - even when it anchors correctly, the media column's width is set by
       three different grid-template-columns rules across two breakpoints
       (2fr 1fr above 1200px, 1fr min(50vw, sidebar) below), so a hand-set
       percentage would be wrong at two of the three sizes.
     - and it would have to be re-set by hand whenever
       desktop_media_position changed.

   Moving the element removes all three problems: it becomes a child of the
   media column, so it inherits that column's real width at every breakpoint
   and follows it to whichever side the setting puts it.


   REV C - THE BADGE LANDED OUTSIDE THE PHOTOGRAPH AT REV A.

   Rev a appended into .product-information__media, which is the whole grid
   COLUMN, not the image. With the gallery's "Extend media to screen edge"
   setting on, that column bleeds past the photograph on one side, so a
   14px inset from its corner put the badge in the page margin - visibly
   left of and above the image.

   Rev c walks a cascade instead, tightest frame first:

     1. .product-media-container - the individual media frame, the element
        that actually carries the 4:5 aspect ratio. This is the photograph.
     2. .product-information__media - the column, as at rev a.

   Falling back rather than failing means a gallery layout that does not
   emit a media container still places the badge somewhere sensible.

   Only the FIRST media container is used. With several images the badge
   stays on the first, which is what the mockup draws.

   PROGRESSIVE ENHANCEMENT, DELIBERATE

   The block renders in place as an ordinary inline row at the top of the
   details column, styled and readable. This script adds
   .aurmina-well--placed, and that class is what switches on the overlay
   positioning. So:

     - no JavaScript: a flag and a size label above the product title.
       Not the mockup, but correct, legible and not broken.
     - script runs: the mockup.

   Nothing is hidden while waiting, which is the failure this project has
   already been bitten by twice - see the registry on the ungated reveal.

   THEME EDITOR

   Re-runs on shopify:section:load, because sections re-render without a page
   reload and a block added mid-edit would otherwise never be promoted. The
   element is checked for its current parent first, so repeated runs are
   harmless and a block moved back out by an editor re-render is picked up
   again.

   Vanilla, no dependencies, no build step, per project section 8.
   ========================================================================== */

(function () {
  'use strict';

  if (window.__aurminaWellOverlay) return;
  window.__aurminaWellOverlay = true;

  /* Tightest frame first. See the rev c note. */
  var TARGETS = ['.product-media-container', '.product-information__media'];
  var GRID_SELECTOR = '.product-information__grid';

  function place(el) {
    if (!el || !el.closest) return;

    var grid = el.closest(GRID_SELECTOR);
    if (!grid) return;

    var media = null;
    for (var t = 0; t < TARGETS.length && !media; t++) {
      media = grid.querySelector(TARGETS[t]);
    }

    /* No media on the product, or the gallery block removed: leave the
       element exactly where it is. The unpromoted styling is a valid
       rendering, so there is nothing to clean up. */
    if (!media) return;

    if (el.parentNode !== media) {
      media.appendChild(el);
    }

    el.classList.add('aurmina-well--placed');
  }

  function run(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var wells = scope.querySelectorAll('[data-aurmina-well]');
    for (var i = 0; i < wells.length; i++) place(wells[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      run(document);
    });
  } else {
    run(document);
  }

  /* Theme editor: sections re-render without a page reload. Scoped to the
     event target where possible, but fall back to the document because a
     re-rendered section replaces the element this block was moved into. */
  document.addEventListener('shopify:section:load', function (event) {
    run(event.target || document);
    run(document);
  });

  document.addEventListener('shopify:block:select', function () {
    run(document);
  });
})();
